<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as AdvancedSearchMultiSearchDialogFormInitialData,
  ValidData as AdvancedSearchMultiSearchDialogFormValidData,
} from '@/components/dialogForms/AdvancedSearchMultiSearchDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import AdvancedSearchMultiSearchDialogForm from '@/components/dialogForms/AdvancedSearchMultiSearchDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (
    formData?: AdvancedSearchMultiSearchDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: AdvancedSearchMultiSearchDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: AdvancedSearchMultiSearchDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<AdvancedSearchMultiSearchDialogFormInitialData>(
  () => {
    return {
      dataType: undefined,
      data: undefined,
    };
  },
);
</script>

<template>
  <PageDialogWrapper
    :class="$style.advancedSearchMultiSearchDialog"
    data-testid="advancedSearchMultiSearchDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Multi-search
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <AdvancedSearchMultiSearchDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-magnify"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Search
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.advancedSearchMultiSearchDialog {
  width: 420px;
}
  .main {
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
